import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase } from '../utils/apiConfig';
import {
  Box, TextField, Button, Snackbar, Alert, Skeleton, Select, MenuItem,
  InputLabel, FormControl, Stepper, Step, StepLabel, Typography, Checkbox, FormControlLabel, Grid, Paper, CircularProgress, Stack,
  Dialog, DialogContent, DialogTitle, IconButton, Slide, FormLabel
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import GooglePlaceAutocompleteElement from '../components/GooglePlaceAutocompleteElement.jsx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
const TestimonialCarousel = lazy(() => import('../components/TestimonialCarousel.jsx'));
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';

const steps = ['Move Details', 'Inventory & Services', 'Select Mover', 'Contact & Review', 'Confirmation'];

const QuoteRequestForm = ({ userId, token, noPaper, origin, destination, setOrigin, setDestination }) => {
  // Provide fallback demo values if not passed as props
  userId = userId || 'demo-user-id';
  token = token || 'demo-token';

  const [activeStep, setActiveStep] = useState(0);
  const [moveDate, setMoveDate] = useState(null);
  const [moveWindow, setMoveWindow] = useState('');
  const [moveSize, setMoveSize] = useState('');
  const [inventory, setInventory] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedMover, setSelectedMover] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [distanceKm, setDistanceKm] = useState(null); // Add distance state

  const params = useParams();
  const navigate = useNavigate();

  const formMethods = useForm({
    mode: 'onSubmit', // Only validate on submit to prevent mobile issues
    defaultValues: {
      origin: '',
      destination: '',
      moveDate: null,
      moveWindow: '',
      moveSize: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      emailTips: false,
    },
  });
  const { control, handleSubmit: formHandleSubmit, watch, setValue, formState: { errors, isValid } } = formMethods;

  // Keep lifted HomePage state in sync with form values so RouteMap updates reliably
  const originVal = watch('origin');
  const destinationVal = watch('destination');
  useEffect(() => {
    if (typeof setOrigin === 'function') setOrigin(originVal || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originVal]);
  useEffect(() => {
    if (typeof setDestination === 'function') setDestination(destinationVal || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationVal]);

  // Rename the original handleSubmit to handleQuoteSubmit
  const handleQuoteSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Ensure inventory is in { id, qty } format
      const inventoryForBackend = inventory.map(item => ({ id: item.id, qty: item.quantity ?? item.qty ?? 1 }));
      await axios.post(`${getApiBase()}/quote-request`, {
        userId,
        origin: data.origin,
        destination: data.destination,
        moveDate: data.moveDate,
        moveWindow: data.moveWindow,
        moveSize: data.moveSize,
        inventory: inventoryForBackend,
        services,
        selectedMover,
        contactName: data.contactName,
        contactPhone: data.contactPhone,
        contactEmail: data.contactEmail,
        emailTips: data.emailTips,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbarMessage('Quote request submitted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate('/inventory');
      return;
    } catch (error) {
      console.error('Error submitting quote request:', error);
      setSnackbarMessage('Error submitting quote request. Please try again later.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch inventory for the user
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const inventoryResponse = await axios.get(`${getApiBase()}/inventory/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInventory(inventoryResponse.data);
      } catch (error) {
        // Fallback mock inventory
        setInventory([
          { id: 'item1', name: 'Sofa', quantity: 1 },
          { id: 'item2', name: 'Bed', quantity: 2 },
          { id: 'item3', name: 'Dining Table', quantity: 1 }
        ]);
        setSnackbarMessage('Could not load inventory from server. Using demo data.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
      // Always use fallback mock services, skip server fetch
      setServices([
        { id: 'service1', name: 'Packing', selected: false },
        { id: 'service2', name: 'Unpacking', selected: false },
        { id: 'service3', name: 'Storage', selected: false }
      ]);
      setIsLoading(false);
    };

    fetchData();
  }, [userId, token]);

  // Helper: Check if address is in BC or Alberta
  function isAddressInBCOrAlberta(address) {
    if (!address) return false;
    const provinceRegex = /(\bBC\b|\bBritish Columbia\b|\bAB\b|\bAlberta\b)/i;
    return provinceRegex.test(address);
  }

  // Helper: Calculate distance between two addresses with client JS (if available) or server fallback
  async function getDistanceKm(originAddress, destinationAddress) {
    // Try browser DistanceMatrix first if Google JS API is available
    if (window.google?.maps?.DistanceMatrixService) {
      const km = await new Promise((resolve) => {
        const service = new window.google.maps.DistanceMatrixService();
        service.getDistanceMatrix({
          origins: [originAddress],
          destinations: [destinationAddress],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
        }, (response, status) => {
          if (status === 'OK' && response?.rows?.[0]?.elements?.[0]?.status === 'OK') {
            const distanceMeters = response.rows[0].elements[0].distance.value;
            resolve(distanceMeters / 1000);
          } else {
            resolve(null);
          }
        });
      });
      if (km != null) return km;
    }
    // Fallback to server-side distance (uses Routes API or demo)
    try {
      const params = new URLSearchParams({ origin: originAddress, destination: destinationAddress, units: 'km' });
      const res = await fetch(`/api/distance?${params.toString()}`);
      if (!res.ok) throw new Error('distance api error');
      const data = await res.json();
      if (typeof data.distanceKm === 'number') return data.distanceKm;
    } catch (e) {
      // ignore and return null
    }
    return null;
  }

  const handleNext = async () => {
    // Use form state for validation
    const values = formMethods.getValues();
    if (activeStep === 0) {
      if (!values.origin || !values.destination || !values.moveDate || !values.moveWindow || !values.moveSize) {
        setSnackbarMessage('Please fill in all move details fields.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      if (!isAddressInBCOrAlberta(values.origin) || !isAddressInBCOrAlberta(values.destination)) {
        setSnackbarMessage('Only addresses in BC or Alberta are allowed.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      setIsLoading(true);
      const distanceKm = await getDistanceKm(values.origin, values.destination);
      setIsLoading(false);
      if (distanceKm === null) {
        setSnackbarMessage('Could not calculate distance between addresses. Please check your entries.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      setDistanceKm(distanceKm);
      sessionStorage.setItem('moveDetails', JSON.stringify({
        origin: values.origin,
        destination: values.destination,
        moveDate: values.moveDate,
        moveWindow: values.moveWindow,
        moveSize: values.moveSize,
        distanceKm
      }));
      navigate('/inventory', { state: {
        origin: values.origin,
        destination: values.destination,
        moveDate: values.moveDate,
        moveWindow: values.moveWindow,
        moveSize: values.moveSize,
        distanceKm
      }});
      return;
    }
    if (activeStep === 2) {
      if (!selectedMover) {
        setSnackbarMessage('Please select a mover.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      // Ensure inventory is in { id, qty } format
      const inventoryForBackend = inventory.map(item => ({ id: item.id, qty: item.quantity ?? item.qty ?? 1 }));
      sessionStorage.setItem('selectedQuote', JSON.stringify({ moverId: selectedMover, price: 1000, mover: { _id: selectedMover, name: selectedMover } }));
      sessionStorage.setItem('moveDetails', JSON.stringify({ origin, destination, moveDate, moveWindow, moveSize, distanceKm }));
      sessionStorage.setItem('inventory', JSON.stringify(inventoryForBackend));
      navigate('/booking');
      return;
    }
    setActiveStep(activeStep + 1);
  };

  // Render the multi-step form inline
  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      bgcolor: 'rgba(255,255,255,0.85)', 
      boxShadow: '0 8px 32px #38bdf84d', 
      borderRadius: 6, 
      p: { xs: 1.5, sm: 2 }, 
      mt: 0, 
      minHeight: 320, 
      maxHeight: { xs: 'none', sm: 600 },
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Removed Progress Bar and Stepper for ultra-compact layout */}
      <form onSubmit={formHandleSubmit(handleQuoteSubmit)}>
        {activeStep === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Stack spacing={1.2}>
              <Controller
                name="origin"
                control={control}
                rules={{ required: false }} // Temporarily disable validation
                render={({ field, fieldState: { error } }) => (
                  <GooglePlaceAutocompleteElement
                    {...field}
                    value={field.value}
                    onChange={val => { field.onChange(val); setOrigin(val); }}
                    placeholder="Origin Address"
                    size="small"
                    error={false} // Force no errors
                    helperText="" // Force no helper text
                  />
                )}
              />
              <Controller
                name="destination"
                control={control}
                rules={{ required: false }} // Temporarily disable validation
                render={({ field, fieldState: { error } }) => (
                  <GooglePlaceAutocompleteElement
                    {...field}
                    value={field.value}
                    onChange={val => { field.onChange(val); setDestination(val); }}
                    placeholder="Destination Address"
                    size="small"
                    error={false} // Force no errors
                    helperText="" // Force no helper text
                  />
                )}
              />
              <Controller
                name="moveDate"
                control={control}
                rules={{ required: 'Move date required' }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    selected={field.value}
                    onChange={field.onChange}
                    minDate={new Date()}
                    customInput={<TextField label="Move Date" fullWidth size="small" />}
                  />
                )}
              />
              <Controller
                name="moveWindow"
                control={control}
                rules={{ required: 'Time window required' }}
                render={({ field }) => (
                  <Select {...field} label="Time Window" fullWidth size="small">
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="Morning (8am-12pm)">Morning (8am-12pm)</MenuItem>
                    <MenuItem value="Afternoon (12pm-4pm)">Afternoon (12pm-4pm)</MenuItem>
                    <MenuItem value="Evening (4pm-8pm)">Evening (4pm-8pm)</MenuItem>
                  </Select>
                )}
              />
              <Controller
                name="moveSize"
                control={control}
                rules={{ required: 'Move size required' }}
                render={({ field }) => (
                  <Select {...field} label="Move Size" fullWidth size="small">
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="Studio">Studio</MenuItem>
                    <MenuItem value="1 Bedroom">1 Bedroom</MenuItem>
                    <MenuItem value="2 Bedroom">2 Bedroom</MenuItem>
                    <MenuItem value="3+ Bedroom">3+ Bedroom</MenuItem>
                    <MenuItem value="Office">Office</MenuItem>
                  </Select>
                )}
              />
            </Stack>
          </motion.div>
        )}
        {/* Step 1 content... */}
        {activeStep === steps.length-1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Stack spacing={1.2}>
              <Controller
                name="contactName"
                control={control}
                rules={{ required: 'Name required' }}
                render={({ field }) => (
                  <TextField {...field} id="contact-name" label="Name" fullWidth required size="small" />
                )}
              />
              <Controller
                name="contactPhone"
                control={control}
                rules={{ required: 'Phone required' }}
                render={({ field }) => (
                  <PhoneInput {...field} country={'ca'} inputStyle={{ width: '100%', fontSize: '0.95rem', padding: '6px 8px' }} />
                )}
              />
              <Controller
                name="contactEmail"
                control={control}
                rules={{ required: 'Email required', pattern: { value: /.+@.+\..+/, message: 'Invalid email' } }}
                render={({ field }) => (
                  <TextField {...field} id="contact-email" label="Email" fullWidth required size="small" />
                )}
              />
              <Controller
                name="emailTips"
                control={control}
                render={({ field }) => (
                  <FormControlLabel control={<Checkbox {...field} checked={field.value} size="small" />} label={<span style={{ fontSize: '0.95rem' }}>Send me moving tips & exclusive offers</span>} />
                )}
              />
            </Stack>
          </motion.div>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep(activeStep-1)} size="small" sx={{ minWidth: 80 }}>Back</Button>
          )}
          {activeStep < steps.length-1 ? (
            <Button variant="contained" color="primary" onClick={handleNext} disabled={!isValid} size="small" sx={{ minWidth: 100 }}>Next</Button>
          ) : (
            <Button variant="contained" color="primary" type="submit" disabled={!isValid} size="small" sx={{ minWidth: 100 }}>Submit</Button>
          )}
        </Box>
      </form>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// HomePage with hero, search, service cards, testimonials
export function HomePage() {
  return (
    <>
      <Hero />
      <Box sx={{ my: 6 }}>
        <ServiceCards />
      </Box>
      <Box sx={{ my: 6 }}>
        <Suspense fallback={<CircularProgress size={24} sx={{ display: 'block', mx: 'auto' }} />}>
          <TestimonialCarousel />
        </Suspense>
      </Box>
    </>
  );
}

export default QuoteRequestForm;